<template>
  <v-container fluid class="lead-statistics pa-0">
    <v-progress-linear v-if="loading" indeterminate height="3" color="#7c6cff" class="dashboard-loader"></v-progress-linear>

    <header class="dashboard-header">
      <div class="header-copy">
        <div class="eyebrow"><span class="eyebrow-dot"></span>Lead Performance</div>
        <h1>Lead-Statistik</h1>
        <p>Alle relevanten Kennzahlen auf einen Blick – mit Membercard und Weiterempfehlungen sauber getrennt.</p>
        <div class="context-row" aria-label="Aktiver Auswertungsbereich">
          <span class="context-pill"><v-icon size="16">mdi-calendar-range</v-icon>{{ periodLabel }}</span>
          <span class="context-pill"><v-icon size="16">mdi-map-marker-multiple-outline</v-icon>{{ studioScopeLabel }}</span>
          <span v-if="selectedCampaigns.length" class="context-pill context-pill--active">
            <v-icon size="16">mdi-filter-check-outline</v-icon>{{ selectedCampaigns.length }} Formulare
          </span>
        </div>
      </div>

      <div class="header-actions">
        <v-menu v-model="filterMenuOpen" offset-y left :close-on-content-click="false" content-class="dashboard-menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" outlined class="dashboard-action" aria-label="Formulare filtern">
              <v-icon left>mdi-tune-variant</v-icon>
              <span class="action-label">Formulare</span>
              <span v-if="selectedCampaigns.length" class="action-count">{{ selectedCampaigns.length }}</span>
            </v-btn>
          </template>
          <v-card dark class="filter-card" width="390" max-width="calc(100vw - 24px)">
            <v-card-title class="filter-title">
              <div>
                <span>Reguläre Formulare</span>
                <small>Membercard bleibt immer separat.</small>
              </div>
              <v-btn icon small aria-label="Filter schliessen" @click="filterMenuOpen = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="selectedCampaigns"
                :items="availableCampaigns"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                outlined
                hide-details
                label="Formulare auswählen"
                no-data-text="Keine Formulare im Zeitraum"
                :menu-props="{ maxHeight: 340 }"></v-select>
              <div class="filter-help">
                Ohne Auswahl werden alle Formulare berücksichtigt. Der Filter wirkt auf KPIs, Charts und Studiovergleich; die Sonderauswertungen
                bleiben bewusst periodweit.
              </div>
            </v-card-text>
            <v-card-actions class="filter-actions">
              <v-btn text color="#b8b0ff" :disabled="!selectedCampaigns.length" @click="resetCampaignFilter">Zurücksetzen</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="#7566f6" depressed @click="filterMenuOpen = false">Übernehmen</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-menu offset-y left content-class="dashboard-menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" depressed class="dashboard-action dashboard-action--primary" aria-label="Statistik exportieren">
              <v-icon left>mdi-tray-arrow-down</v-icon><span class="action-label">Export</span>
            </v-btn>
          </template>
          <v-list dark class="export-list">
            <v-list-item :disabled="loading" @click="downloadExcel()">
              <v-list-item-icon><v-icon color="#52d7a8">mdi-file-excel-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Lead-Rohdaten</v-list-item-title>
                <v-list-item-subtitle>Alle Formulare inklusive Membercard</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item :disabled="loading || !selectedCampaigns.length" @click="downloadSelectedExcel">
              <v-list-item-icon><v-icon color="#42a5f5">mdi-filter-check-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Lead-Rohdaten – Auswahl</v-list-item-title>
                <v-list-item-subtitle>
                  {{ selectedCampaigns.length ? `${selectedCampaigns.length} ausgewählte Formulare` : "Zuerst Formulare auswählen" }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item :disabled="loading" @click="downloadStudioConversionExcel">
              <v-list-item-icon><v-icon color="#9b90ff">mdi-table-arrow-down</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Studio Conversion Liste</v-list-item-title>
                <v-list-item-subtitle>Zeitraum und Formularfilter</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <div v-if="loadError" class="state-card state-card--error" role="alert">
      <v-icon color="#ff7d8f">mdi-alert-circle-outline</v-icon>
      <div><strong>Die Statistik konnte nicht geladen werden.</strong><span>Bitte versuche es nochmals.</span></div>
      <v-btn text color="#b8b0ff" @click="fetchLeadStatistics">Erneut laden</v-btn>
    </div>

    <div v-else-if="datePickerRange.length !== 2" class="state-card state-card--empty">
      <v-icon color="#9b90ff">mdi-calendar-search</v-icon>
      <div><strong>Zeitraum auswählen</strong><span>Wähle ein Start- und Enddatum, um die Lead-Performance zu laden.</span></div>
    </div>

    <template v-else>
      <div class="definition-strip">
        <div class="definition-icon"><v-icon color="#b8b0ff">mdi-calculator-variant-outline</v-icon></div>
        <div class="definition-copy">
          <strong>Conversion gesamt = Leads mit Vertragsstatus / alle Leads ohne Membercard</strong>
          <span>„Geschlossen / nicht erreicht“ bleibt bewusst enthalten und macht die Leadqualität sichtbar.</span>
        </div>
        <v-tooltip bottom max-width="360">
          <template v-slot:activator="{ on, attrs }">
            <button v-bind="attrs" v-on="on" type="button" class="info-button" aria-label="Conversion-Definition erklären">
              <v-icon size="19">mdi-information-outline</v-icon>
            </button>
          </template>
          <span>Zusätzlich zeigen wir die Conversion ohne geschlossene Leads. So bleiben Gesamtqualität und Verkaufsleistung vergleichbar.</span>
        </v-tooltip>
      </div>

      <section class="kpi-grid" aria-label="Wichtigste Lead-Kennzahlen">
        <article class="kpi-card kpi-card--purple">
          <div class="kpi-topline"><span>Auswertbare Leads</span><v-icon>mdi-account-multiple-outline</v-icon></div>
          <div class="kpi-value">{{ formatNumber(filteredSummary.totalLeads) }}</div>
          <div class="kpi-meta">Alle gewählten Formulare · ohne Membercard</div>
        </article>

        <article class="kpi-card kpi-card--green">
          <div class="kpi-topline"><span>Leads mit Vertrag</span><v-icon>mdi-check-decagram-outline</v-icon></div>
          <div class="kpi-value">{{ formatNumber(filteredSummary.contracts) }}</div>
          <div class="kpi-meta">Aktueller Status „Vertrag abgeschlossen“</div>
        </article>

        <article class="kpi-card kpi-card--blue">
          <div class="kpi-topline"><span>Conversion gesamt</span><v-icon>mdi-chart-line-variant</v-icon></div>
          <div class="kpi-value">{{ formatRate(filteredSummary.conversionRate) }}</div>
          <div class="kpi-meta">Ohne Geschlossene: {{ formatRate(filteredSummary.reachableConversionRate) }}</div>
        </article>

        <article class="kpi-card kpi-card--orange">
          <div class="kpi-topline"><span>Geschlossen / nicht erreicht</span><v-icon>mdi-phone-off-outline</v-icon></div>
          <div class="kpi-value">{{ formatNumber(filteredSummary.closedLeads) }}</div>
          <div class="kpi-meta">{{ formatRate(closedRate) }} aller auswertbaren Leads</div>
        </article>
      </section>

      <section class="dashboard-section">
        <div class="section-heading">
          <div>
            <span class="section-kicker">Reguläre Lead-Performance</span>
            <h2>Wo entstehen Leads – und was wird daraus?</h2>
            <p>Membercard ist in diesem gesamten Bereich ausgeschlossen. Weiterempfehlungen bleiben als echte Leads enthalten.</p>
          </div>
          <span class="scope-badge">{{ campaignScopeLabel }}</span>
        </div>

        <div class="analytics-grid">
          <article class="panel campaign-panel">
            <div class="panel-heading">
              <div>
                <span class="panel-kicker">Formularvergleich</span>
                <h3>Leads nach aktuellem Status</h3>
              </div>
              <span v-if="campaignChartRows.length < filteredCampaigns.length" class="panel-note"
                >Top {{ campaignChartRows.length }} nach Volumen</span
              >
            </div>
            <div v-if="campaignChartSeries.length" class="chart-wrap">
              <apexchart type="bar" :height="campaignChartHeight" :options="campaignChartOptions" :series="campaignChartSeries"></apexchart>
            </div>
            <div v-else class="empty-state"><v-icon>mdi-chart-bar-stacked</v-icon><span>Keine regulären Leads für diese Auswahl.</span></div>
          </article>

          <article class="panel status-panel">
            <div class="panel-heading">
              <div>
                <span class="panel-kicker">Momentaufnahme</span>
                <h3>Aktueller Bearbeitungsstand</h3>
              </div>
            </div>
            <div v-if="filteredSummary.totalLeads" class="status-list">
              <div v-for="status in statusRows" :key="status.key" class="status-row">
                <div class="status-row__top">
                  <span><i :style="{ backgroundColor: status.color }"></i>{{ status.label }}</span>
                  <strong
                    >{{ formatNumber(status.count) }} <small>{{ formatRate(status.share) }}</small></strong
                  >
                </div>
                <div class="status-track"><span :style="{ width: status.share + '%', backgroundColor: status.color }"></span></div>
              </div>
            </div>
            <div v-else class="empty-state empty-state--compact"><v-icon>mdi-chart-donut</v-icon><span>Keine Statusdaten vorhanden.</span></div>
            <div class="panel-footnote">
              <v-icon size="15">mdi-history</v-icon>Die Statuswerte zeigen den heutigen Stand der Leads aus dem Zeitraum.
            </div>
          </article>
        </div>

        <article class="panel trend-panel">
          <div class="panel-heading panel-heading--responsive">
            <div>
              <span class="panel-kicker">Eingang nach Datum</span>
              <h3>Lead-Entwicklung im Zeitraum</h3>
            </div>
            <v-btn-toggle v-model="trendMode" mandatory dense rounded dark class="mode-toggle">
              <v-btn value="total" small dark>Gesamt</v-btn>
              <v-btn value="split" small dark>Formulare</v-btn>
            </v-btn-toggle>
          </div>
          <div v-if="trendSeries.length" class="chart-wrap chart-wrap--trend">
            <apexchart :type="trendMode === 'total' ? 'area' : 'line'" height="310" :options="trendChartOptions" :series="trendSeries"></apexchart>
          </div>
          <div v-else class="empty-state"><v-icon>mdi-chart-timeline-variant-shimmer</v-icon><span>Keine Zeitreihe für diese Auswahl.</span></div>
          <div v-if="trendSeriesLimited" class="panel-footnote">
            <v-icon size="15">mdi-eye-outline</v-icon>Zur Lesbarkeit werden die sechs grössten Formulare gezeigt.
          </div>
        </article>

        <article class="panel campaign-table-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-kicker">Alle Formulare</span>
              <h3>Conversion im Detail</h3>
            </div>
            <span class="panel-note">Geschlossene Leads sind in „Gesamt“ enthalten</span>
          </div>
          <v-data-table
            :headers="campaignHeaders"
            :items="filteredCampaigns"
            :items-per-page="8"
            :footer-props="{ itemsPerPageText: 'Zeilen', itemsPerPageOptions: [8, 15, -1] }"
            no-data-text="Keine Formulardaten vorhanden"
            class="dashboard-table campaign-table"
            dark>
            <template v-slot:item.name="{ item }">
              <div class="name-cell">
                <strong>{{ item.name }}</strong>
                <div>
                  <span v-if="item.isReferral" class="micro-tag micro-tag--referral">Weiterempfehlung</span>
                  <span v-if="item.isElevenDay" class="micro-tag micro-tag--membercard">11-Tage</span>
                </div>
              </div>
            </template>
            <template v-slot:item.contracts="{ item }"
              ><strong class="positive-number">{{ formatNumber(item.contracts) }}</strong></template
            >
            <template v-slot:item.closedLeads="{ item }"
              ><span :class="item.closedLeads ? 'warning-number' : 'muted-number'">{{ formatNumber(item.closedLeads) }}</span></template
            >
            <template v-slot:item.conversionRate="{ item }"
              ><span class="rate-pill">{{ formatRate(item.conversionRate) }}</span></template
            >
            <template v-slot:item.reachableConversionRate="{ item }"
              ><span class="secondary-rate">{{ formatRate(item.reachableConversionRate) }}</span></template
            >
          </v-data-table>
        </article>
      </section>

      <section class="dashboard-section special-section">
        <div class="section-heading">
          <div>
            <span class="section-kicker">Sonderauswertungen</span>
            <h2>Membercard und Weiterempfehlungen getrennt betrachtet</h2>
            <p>
              Beide Bereiche bleiben sichtbar, ohne die reguläre Statistik zu verfälschen, und nutzen unabhängig vom Formularfilter den gesamten
              gewählten Zeitraum.
            </p>
          </div>
        </div>

        <div class="special-grid">
          <article class="special-card special-card--membercard">
            <div class="special-card__header">
              <div class="special-icon"><v-icon>mdi-card-account-details-star-outline</v-icon></div>
              <div>
                <span>11 Tage · im Studio aktiviert</span>
                <h3>Membercard-Leads</h3>
              </div>
              <span class="special-label">Separat</span>
            </div>
            <p class="special-intro">
              Eine Membercard steht für die 11 Gratis-Trainingstage und wird vom Trainerteam direkt im Studio aktiviert. Hier wird der aktuelle Status
              dieser Membercards ausgewertet; sie bleiben aus der regulären Lead-Conversion ausgeschlossen.
            </p>

            <div class="special-kpis">
              <div>
                <span>Membercard-Leads</span><strong>{{ formatNumber(membercardData.totalLeads) }}</strong>
              </div>
              <div>
                <span>Mit Vertrag</span><strong>{{ formatNumber(membercardData.contracts) }}</strong>
              </div>
              <div>
                <span>Conversion</span><strong>{{ formatRate(membercardData.conversionRate) }}</strong>
              </div>
              <div>
                <span>Nicht erreicht</span><strong>{{ formatNumber(membercardData.closedLeads) }}</strong>
              </div>
            </div>

            <div class="mini-list-heading"><span>Performance nach Studio</span><small>nach Membercard-Volumen</small></div>
            <div v-if="visibleMembercardStudios.length" class="ranking-list ranking-list--membercard">
              <div v-for="studio in visibleMembercardStudios" :key="studio.id" class="ranking-row">
                <div class="ranking-row__top">
                  <strong>{{ studio.name }}</strong>
                  <b>{{ formatRate(studio.conversionRate) }}</b>
                </div>
                <div class="ranking-row__details">
                  <span>{{ formatNumber(studio.totalLeads) }} Membercards</span>
                  <span>{{ formatNumber(studio.contracts) }} mit Vertrag</span>
                  <span>{{ formatNumber(studio.closedLeads) }} nicht erreicht</span>
                </div>
                <div class="ranking-track"><span :style="{ width: membercardBarWidth(studio.totalLeads) + '%' }"></span></div>
              </div>
            </div>
            <div v-else class="special-empty">Keine Membercard-Leads im gewählten Zeitraum.</div>
            <v-btn
              v-if="membercardStudioRows.length > membercardStudioLimit"
              text
              small
              color="#c2bbff"
              class="show-more"
              @click="showAllMembercardStudios = !showAllMembercardStudios">
              {{ showAllMembercardStudios ? "Weniger anzeigen" : "Alle Studios anzeigen" }}
              <v-icon right small>{{ showAllMembercardStudios ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </v-btn>

            <div class="data-note data-note--membercard">
              <v-icon size="18">mdi-check-circle-outline</v-icon>
              <span
                >Conversion = Membercard-Leads mit Vertrag / alle Membercard-Leads. Sie ist keine Quote zwischen 11-Tage-Leads und Membercards.</span
              >
            </div>
          </article>

          <article class="special-card special-card--referral">
            <div class="special-card__header">
              <div class="special-icon"><v-icon>mdi-account-heart-outline</v-icon></div>
              <div>
                <span>Kunden werben Interessenten</span>
                <h3>Weiterempfehlungen</h3>
              </div>
              <span class="special-label">Eigener Kanal</span>
            </div>
            <p class="special-intro">
              In der ungefilterten Gesamtansicht zählen Referral-Leads regulär mit; hier werden sie zusätzlich periodweit als eigener
              Akquisitionskanal ausgewertet.
            </p>

            <div class="special-kpis">
              <div>
                <span>Leads</span><strong>{{ formatNumber(referralData.totalLeads) }}</strong>
              </div>
              <div>
                <span>Mit Vertrag</span><strong>{{ formatNumber(referralData.contracts) }}</strong>
              </div>
              <div>
                <span>Conversion</span><strong>{{ formatRate(referralData.conversionRate) }}</strong>
              </div>
              <div>
                <span>Nicht erreicht</span><strong>{{ formatNumber(referralData.closedLeads) }}</strong>
              </div>
            </div>

            <div class="mini-list-heading"><span>Performance nach Studio</span><small>nach Leadvolumen</small></div>
            <div v-if="visibleReferralStudios.length" class="ranking-list">
              <div v-for="studio in visibleReferralStudios" :key="studio.id" class="ranking-row">
                <div class="ranking-row__top">
                  <strong>{{ studio.name }}</strong>
                  <span
                    >{{ formatNumber(studio.totalLeads) }} Leads · <b>{{ formatRate(studio.conversionRate) }}</b></span
                  >
                </div>
                <div class="ranking-track"><span :style="{ width: referralBarWidth(studio.totalLeads) + '%' }"></span></div>
              </div>
            </div>
            <div v-else class="special-empty">Keine Weiterempfehlungen im gewählten Zeitraum.</div>
            <v-btn
              v-if="referralStudioRows.length > referralStudioLimit"
              text
              small
              color="#86e7c3"
              class="show-more"
              @click="showAllReferralStudios = !showAllReferralStudios">
              {{ showAllReferralStudios ? "Weniger anzeigen" : "Alle Studios anzeigen" }}
              <v-icon right small>{{ showAllReferralStudios ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </v-btn>

            <div class="data-note data-note--referral">
              <v-icon size="18">mdi-check-circle-outline</v-icon
              ><span>Erkannt über Referral-Kennzeichnung oder „Weiterempfehlung“ im Formularnamen.</span>
            </div>
          </article>
        </div>
      </section>

      <section class="dashboard-section studio-section">
        <div class="section-heading section-heading--controls">
          <div>
            <span class="section-kicker">Studiovergleich</span>
            <h2>Conversion pro Studio</h2>
            <p>Eine Zeile pro ausgewähltem Studio – auch wenn im Zeitraum keine Leads eingegangen sind.</p>
          </div>
          <div class="table-controls">
            <v-text-field
              v-model="studioSearch"
              dark
              color="#7c6cff"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="Studio suchen"></v-text-field>
            <v-select
              v-model="studioSort"
              dark
              color="#7c6cff"
              :items="studioSortOptions"
              :menu-props="{ dark: true }"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              label="Sortierung"></v-select>
          </div>
        </div>

        <article class="panel studio-table-panel">
          <div class="table-scope-note">
            <v-icon size="17">mdi-shield-check-outline</v-icon>Membercard ausgeschlossen · Geschlossen in Gesamtconversion enthalten ·
            {{ campaignScopeLabel }} · Zeitraum {{ periodLabel }}
          </div>
          <v-data-table
            :headers="studioHeaders"
            :items="studioRows"
            :items-per-page="15"
            :footer-props="{ itemsPerPageText: 'Studios', itemsPerPageOptions: [15, 30, -1] }"
            no-data-text="Kein Studio gefunden"
            class="dashboard-table studio-table studio-table--desktop"
            dark>
            <template v-slot:item.name="{ item }"
              ><div class="studio-name">
                <span>{{ studioInitials(item.name) }}</span
                ><strong>{{ item.name }}</strong>
              </div></template
            >
            <template v-slot:item.contracts="{ item }"
              ><strong class="positive-number">{{ formatNumber(item.contracts) }}</strong></template
            >
            <template v-slot:item.closedLeads="{ item }"
              ><span :class="item.closedLeads ? 'warning-number' : 'muted-number'">{{ formatNumber(item.closedLeads) }}</span></template
            >
            <template v-slot:item.conversionRate="{ item }"
              ><span class="rate-pill">{{ formatRate(item.conversionRate) }}</span></template
            >
            <template v-slot:item.reachableConversionRate="{ item }"
              ><span class="secondary-rate">{{ formatRate(item.reachableConversionRate) }}</span></template
            >
            <template v-slot:item.membercards="{ item }"
              ><span class="special-count special-count--membercard">{{ formatNumber(item.membercards) }}</span></template
            >
            <template v-slot:item.referralLeads="{ item }"
              ><span class="special-count special-count--referral">{{ formatNumber(item.referralLeads) }}</span></template
            >
          </v-data-table>

          <div class="studio-mobile-grid">
            <article v-for="studio in studioRows" :key="studio.id" class="studio-mobile-card">
              <div class="studio-mobile-card__header">
                <div class="studio-name">
                  <span>{{ studioInitials(studio.name) }}</span
                  ><strong>{{ studio.name }}</strong>
                </div>
                <div class="mobile-conversion">
                  <small>Conversion</small><strong>{{ formatRate(studio.conversionRate) }}</strong>
                </div>
              </div>
              <div class="studio-mobile-stats">
                <div>
                  <span>Leads</span><strong>{{ formatNumber(studio.totalLeads) }}</strong>
                </div>
                <div>
                  <span>Mit Vertrag</span><strong class="positive-number">{{ formatNumber(studio.contracts) }}</strong>
                </div>
                <div>
                  <span>Nicht erreicht</span><strong>{{ formatNumber(studio.closedLeads) }}</strong>
                </div>
                <div>
                  <span>Conv. ohne Geschlossene</span><strong>{{ formatRate(studio.reachableConversionRate) }}</strong>
                </div>
              </div>
              <div class="studio-mobile-specials">
                <span><i class="dot-membercard"></i>{{ formatNumber(studio.membercards) }} Membercards</span>
                <span><i class="dot-referral"></i>{{ formatNumber(studio.referralLeads) }} Empfehlungen</span>
              </div>
            </article>
            <div v-if="!studioRows.length" class="empty-state"><v-icon>mdi-map-marker-off-outline</v-icon><span>Kein Studio gefunden.</span></div>
          </div>
        </article>
      </section>

      <footer class="dashboard-footnote">
        <v-icon size="16">mdi-information-slab-circle-outline</v-icon>
        <span
          >Die Auswertung gruppiert Leads nach ihrem Erstellungsdatum im gewählten Zeitraum und zeigt deren aktuellen Status. Sie zählt konvertierte
          Leads, nicht eindeutige Verträge oder Personen.</span
        >
      </footer>
    </template>
  </v-container>
</template>

<script>
import api from "@/api"

const STATUS_DEFINITIONS = [
  { key: "Wurde angeworben", label: "Neu eingegangen", color: "#7c6cff" },
  { key: "Kontaktiert", label: "Kontaktiert", color: "#42a5f5" },
  { key: "Probetraining vereinbart", label: "Probetraining", color: "#e2a84a" },
  { key: "Vertrag abgeschlossen", label: "Vertrag abgeschlossen", color: "#42d5a0" },
  { key: "Kein Abschluss", label: "Kein Abschluss", color: "#f07485" },
  { key: "Geschlossen", label: "Geschlossen / nicht erreicht", color: "#778196" },
]

function createEmptySummary() {
  return {
    totalLeads: 0,
    contracts: 0,
    closedLeads: 0,
    reachableLeads: 0,
    noContractLeads: 0,
    conversionRate: null,
    reachableConversionRate: null,
    reachabilityRate: null,
    statusCounts: {},
  }
}

function createEmptyStatistics() {
  return {
    meta: {},
    summary: createEmptySummary(),
    campaignPerformance: [],
    membercard: {
      ...createEmptySummary(),
      elevenDayLeads: 0,
      membercards: 0,
      elevenDayContracts: 0,
      byStudio: [],
    },
    referrals: { ...createEmptySummary(), byStudio: [] },
    studioPerformance: [],
    leadsPerSettingAndTime: [],
    regularLeadsPerSettingAndTime: [],
  }
}

function calculateRate(numerator, denominator) {
  if (!denominator) return null
  return Number(((numerator / denominator) * 100).toFixed(1))
}

export default {
  name: "LeadStatisticsDashboard",
  props: {
    datePickerRange: {
      type: Array,
      default: () => [],
    },
    studios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      loadError: false,
      requestId: 0,
      filterMenuOpen: false,
      selectedCampaigns: [],
      trendMode: "total",
      studioSearch: "",
      studioSort: "name",
      showAllMembercardStudios: false,
      showAllReferralStudios: false,
      membercardStudioLimit: 6,
      referralStudioLimit: 7,
      statisticInfo: createEmptyStatistics(),
      studioSortOptions: [
        { text: "Studio A–Z", value: "name" },
        { text: "Höchste Conversion", value: "conversion" },
        { text: "Meiste Leads", value: "leads" },
        { text: "Meiste Verträge", value: "contracts" },
      ],
      campaignHeaders: [
        { text: "Formular", value: "name", align: "start" },
        { text: "Leads", value: "totalLeads", align: "end" },
        { text: "Mit Vertrag", value: "contracts", align: "end" },
        { text: "Nicht erreicht", value: "closedLeads", align: "end" },
        { text: "Conversion gesamt", value: "conversionRate", align: "end" },
        { text: "Ohne Geschlossene", value: "reachableConversionRate", align: "end" },
      ],
      studioHeaders: [
        { text: "Studio", value: "name", align: "start", sortable: false },
        { text: "Leads", value: "totalLeads", align: "end", sortable: false },
        { text: "Mit Vertrag", value: "contracts", align: "end", sortable: false },
        { text: "Nicht erreicht", value: "closedLeads", align: "end", sortable: false },
        { text: "Conversion gesamt", value: "conversionRate", align: "end", sortable: false },
        { text: "Ohne Geschlossene", value: "reachableConversionRate", align: "end", sortable: false },
        { text: "Membercards", value: "membercards", align: "end", sortable: false },
        { text: "Empfehlungen", value: "referralLeads", align: "end", sortable: false },
      ],
    }
  },
  computed: {
    periodLabel() {
      if (this.datePickerRange.length !== 2) return "Kein Zeitraum gewählt"
      return `${this.formatDate(this.datePickerRange[0])} – ${this.formatDate(this.datePickerRange[1])}`
    },
    studioScopeLabel() {
      if (!this.studios.length) return "Keine Studios gewählt"
      if (this.studios.length === 1) return this.studios[0].name || "1 Studio"
      return `${this.studios.length} Studios`
    },
    availableCampaigns() {
      return this.statisticInfo.campaignPerformance.map((campaign) => campaign.name).sort((a, b) => a.localeCompare(b, "de"))
    },
    filteredCampaigns() {
      const campaigns = this.statisticInfo.campaignPerformance || []
      if (!this.selectedCampaigns.length) return campaigns
      const selected = new Set(this.selectedCampaigns)
      return campaigns.filter((campaign) => selected.has(campaign.name))
    },
    campaignScopeLabel() {
      if (!this.selectedCampaigns.length) return `Alle ${this.availableCampaigns.length} Formulare`
      return `${this.filteredCampaigns.length} von ${this.availableCampaigns.length} Formularen`
    },
    filteredSummary() {
      const statusCounts = {}
      let totalLeads = 0
      let contracts = 0
      let closedLeads = 0
      let noContractLeads = 0

      this.filteredCampaigns.forEach((campaign) => {
        totalLeads += Number(campaign.totalLeads) || 0
        contracts += Number(campaign.contracts) || 0
        closedLeads += Number(campaign.closedLeads) || 0
        noContractLeads += Number(campaign.noContractLeads) || 0
        Object.keys(campaign.statusCounts || {}).forEach((status) => {
          statusCounts[status] = (statusCounts[status] || 0) + (Number(campaign.statusCounts[status]) || 0)
        })
      })

      const reachableLeads = Math.max(totalLeads - closedLeads, 0)
      return {
        totalLeads,
        contracts,
        closedLeads,
        reachableLeads,
        noContractLeads,
        conversionRate: calculateRate(contracts, totalLeads),
        reachableConversionRate: calculateRate(contracts, reachableLeads),
        statusCounts,
      }
    },
    closedRate() {
      return calculateRate(this.filteredSummary.closedLeads, this.filteredSummary.totalLeads)
    },
    statusRows() {
      const total = this.filteredSummary.totalLeads
      const knownKeys = new Set(STATUS_DEFINITIONS.map((status) => status.key))
      const rows = STATUS_DEFINITIONS.map((status) => {
        const count = this.filteredSummary.statusCounts[status.key] || 0
        return { ...status, count, share: calculateRate(count, total) || 0 }
      })
      const otherCount = Object.keys(this.filteredSummary.statusCounts)
        .filter((status) => !knownKeys.has(status))
        .reduce((sum, status) => sum + this.filteredSummary.statusCounts[status], 0)
      if (otherCount) {
        rows.push({
          key: "Weitere Status",
          label: "Weitere Status",
          color: "#a78bba",
          count: otherCount,
          share: calculateRate(otherCount, total) || 0,
        })
      }
      return rows
    },
    campaignChartRows() {
      return [...this.filteredCampaigns].sort((a, b) => b.totalLeads - a.totalLeads).slice(0, 10)
    },
    campaignChartSeries() {
      const knownKeys = new Set(STATUS_DEFINITIONS.map((status) => status.key))
      const series = STATUS_DEFINITIONS.map((status) => ({
        name: status.label,
        statusKey: status.key,
        color: status.color,
        data: this.campaignChartRows.map((campaign) => (campaign.statusCounts && campaign.statusCounts[status.key]) || 0),
      })).filter((entry) => entry.data.some((value) => value > 0))

      const otherData = this.campaignChartRows.map((campaign) =>
        Object.keys(campaign.statusCounts || {})
          .filter((status) => !knownKeys.has(status))
          .reduce((sum, status) => sum + campaign.statusCounts[status], 0),
      )
      if (otherData.some((value) => value > 0))
        series.push({ name: "Weitere Status", statusKey: "Weitere Status", color: "#a78bba", data: otherData })
      return series
    },
    campaignChartHeight() {
      return Math.max(300, this.campaignChartRows.length * 48 + 90)
    },
    campaignChartOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false }, background: "transparent", foreColor: "#aab2c2", animations: { speed: 380 } },
        colors: this.campaignChartSeries.map((series) => series.color),
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "66%" } },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        xaxis: {
          categories: this.campaignChartRows.map((campaign) => campaign.name),
          labels: { style: { colors: "#8f98aa", fontSize: "11px" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#dce1eb", fontSize: "12px", fontWeight: 500 }, maxWidth: 210 } },
        grid: { borderColor: "rgba(255,255,255,.07)", strokeDashArray: 4, padding: { left: 8, right: 14 } },
        legend: { position: "top", horizontalAlign: "left", labels: { colors: "#aab2c2" }, markers: { radius: 10 } },
        tooltip: { theme: "dark", shared: true, intersect: false },
      }
    },
    trendAvailableSeries() {
      const allowedCampaigns = new Set(this.filteredCampaigns.map((campaign) => campaign.name))
      return (this.statisticInfo.regularLeadsPerSettingAndTime || [])
        .filter((series) => allowedCampaigns.has(series.name))
        .map((series) => ({
          name: series.name,
          data: [...(series.data || [])].sort((a, b) => String(a.x).localeCompare(String(b.x))),
          total: (series.data || []).reduce((sum, point) => sum + (Number(point.y) || 0), 0),
        }))
    },
    trendSeries() {
      if (this.trendMode === "split") {
        return [...this.trendAvailableSeries]
          .sort((a, b) => b.total - a.total)
          .slice(0, 6)
          .map((series) => ({ name: series.name, data: series.data }))
      }

      const totals = {}
      this.trendAvailableSeries.forEach((series) => {
        series.data.forEach((point) => {
          totals[point.x] = (totals[point.x] || 0) + (Number(point.y) || 0)
        })
      })
      const data = Object.keys(totals)
        .sort()
        .map((date) => ({ x: date, y: totals[date] }))
      return data.length ? [{ name: "Reguläre Leads", data }] : []
    },
    trendSeriesLimited() {
      return this.trendMode === "split" && this.trendAvailableSeries.length > 6
    },
    trendChartOptions() {
      const split = this.trendMode === "split"
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, background: "transparent", foreColor: "#aab2c2" },
        colors: split ? ["#7c6cff", "#42a5f5", "#42d5a0", "#e2a84a", "#f07485", "#b77cff"] : ["#7c6cff"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: split ? 2.5 : 3 },
        fill: split
          ? { type: "solid", opacity: 1 }
          : { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.42, opacityTo: 0.03, stops: [0, 92, 100] } },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: "#8f98aa" }, datetimeFormatter: { year: "yyyy", month: "MMM", day: "dd.MM" } },
          axisBorder: { color: "rgba(255,255,255,.08)" },
          axisTicks: { color: "rgba(255,255,255,.08)" },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: "#8f98aa" }, formatter: (value) => Math.round(value) } },
        grid: { borderColor: "rgba(255,255,255,.07)", strokeDashArray: 4 },
        legend: { show: split, position: "top", horizontalAlign: "left", labels: { colors: "#aab2c2" } },
        tooltip: { theme: "dark", x: { format: "dd.MM.yyyy" }, y: { formatter: (value) => `${Math.round(value)} Leads` } },
      }
    },
    membercardData() {
      return this.statisticInfo.membercard || createEmptyStatistics().membercard
    },
    membercardStudioRows() {
      return [...(this.membercardData.byStudio || [])]
        .filter((studio) => studio.totalLeads)
        .sort((a, b) => b.totalLeads - a.totalLeads || a.name.localeCompare(b.name, "de"))
    },
    visibleMembercardStudios() {
      return this.showAllMembercardStudios ? this.membercardStudioRows : this.membercardStudioRows.slice(0, this.membercardStudioLimit)
    },
    referralData() {
      return this.statisticInfo.referrals || createEmptyStatistics().referrals
    },
    referralStudioRows() {
      return [...(this.referralData.byStudio || [])]
        .filter((studio) => studio.totalLeads)
        .sort((a, b) => b.totalLeads - a.totalLeads || a.name.localeCompare(b.name, "de"))
    },
    visibleReferralStudios() {
      return this.showAllReferralStudios ? this.referralStudioRows : this.referralStudioRows.slice(0, this.referralStudioLimit)
    },
    maxReferralLeads() {
      return this.referralStudioRows.reduce((max, studio) => Math.max(max, studio.totalLeads), 0)
    },
    maxMembercardLeads() {
      return this.membercardStudioRows.reduce((max, studio) => Math.max(max, studio.totalLeads), 0)
    },
    filteredStudioPerformance() {
      const studioAggregates = new Map(
        (this.statisticInfo.studioPerformance || []).map((studio) => [
          String(studio.id),
          { statusCounts: {}, referralLeads: 0, referralContracts: 0, fallback: studio },
        ]),
      )

      this.filteredCampaigns.forEach((campaign) => {
        const campaignStudios = campaign.byStudio || []
        campaignStudios.forEach((studio) => {
          const id = String(studio.id)
          if (!studioAggregates.has(id)) {
            studioAggregates.set(id, { statusCounts: {}, referralLeads: 0, referralContracts: 0, fallback: studio })
          }
          const aggregate = studioAggregates.get(id)
          Object.keys(studio.statusCounts || {}).forEach((status) => {
            aggregate.statusCounts[status] = (aggregate.statusCounts[status] || 0) + (Number(studio.statusCounts[status]) || 0)
          })
          if (campaign.isReferral) {
            aggregate.referralLeads += Number(studio.totalLeads) || 0
            aggregate.referralContracts += Number(studio.contracts) || 0
          }
        })
      })

      return Array.from(studioAggregates.values()).map(({ statusCounts, referralLeads, referralContracts, fallback }) => {
        const totalLeads = Object.values(statusCounts).reduce((sum, count) => sum + count, 0)
        const contracts = statusCounts["Vertrag abgeschlossen"] || 0
        const closedLeads = statusCounts.Geschlossen || 0
        const reachableLeads = Math.max(totalLeads - closedLeads, 0)
        return {
          ...fallback,
          totalLeads,
          contracts,
          closedLeads,
          reachableLeads,
          noContractLeads: statusCounts["Kein Abschluss"] || 0,
          conversionRate: calculateRate(contracts, totalLeads),
          reachableConversionRate: calculateRate(contracts, reachableLeads),
          reachabilityRate: calculateRate(reachableLeads, totalLeads),
          statusCounts,
          referralLeads,
          referralContracts,
        }
      })
    },
    studioRows() {
      const query = String(this.studioSearch || "")
        .trim()
        .toLowerCase()
      const rows = this.filteredStudioPerformance.filter((studio) => !query || String(studio.name).toLowerCase().includes(query))
      return [...rows].sort((a, b) => {
        if (this.studioSort === "conversion")
          return this.compareNullableRates(b.conversionRate, a.conversionRate) || a.name.localeCompare(b.name, "de")
        if (this.studioSort === "leads") return b.totalLeads - a.totalLeads || a.name.localeCompare(b.name, "de")
        if (this.studioSort === "contracts") return b.contracts - a.contracts || a.name.localeCompare(b.name, "de")
        return a.name.localeCompare(b.name, "de")
      })
    },
  },
  created() {
    if (this.datePickerRange.length === 2) this.fetchLeadStatistics()
  },
  methods: {
    normalizeStatistics(response) {
      const empty = createEmptyStatistics()
      const source = response || {}
      const studioMap = new Map(
        (Array.isArray(source.studioPerformance) ? source.studioPerformance : []).map((studio) => [String(studio.id), studio]),
      )
      this.studios.forEach((studio) => {
        const id = String(studio._id)
        if (studioMap.has(id)) return
        studioMap.set(id, {
          id,
          name: studio.name || "Unbenanntes Studio",
          ...createEmptySummary(),
          membercards: 0,
          elevenDayLeads: 0,
          elevenDayContracts: 0,
          referralLeads: 0,
          referralContracts: 0,
        })
      })
      return {
        ...empty,
        ...source,
        meta: { ...empty.meta, ...(source.meta || {}) },
        summary: { ...empty.summary, ...(source.summary || {}) },
        membercard: { ...empty.membercard, ...(source.membercard || {}), byStudio: (source.membercard && source.membercard.byStudio) || [] },
        referrals: { ...empty.referrals, ...(source.referrals || {}), byStudio: (source.referrals && source.referrals.byStudio) || [] },
        campaignPerformance: Array.isArray(source.campaignPerformance) ? source.campaignPerformance : [],
        studioPerformance: Array.from(studioMap.values()),
        leadsPerSettingAndTime: Array.isArray(source.leadsPerSettingAndTime) ? source.leadsPerSettingAndTime : [],
        regularLeadsPerSettingAndTime: Array.isArray(source.regularLeadsPerSettingAndTime) ? source.regularLeadsPerSettingAndTime : [],
      }
    },
    async fetchLeadStatistics() {
      if (this.datePickerRange.length !== 2) {
        this.resetStatistics()
        return
      }
      const currentRequestId = ++this.requestId
      this.loading = true
      this.loadError = false

      try {
        const response = await api.getLeadStatistics({
          start_date: this.datePickerRange[0],
          end_date: this.datePickerRange[1],
          studios: this.studios.map((studio) => studio._id),
        })
        if (currentRequestId !== this.requestId) return
        this.statisticInfo = this.normalizeStatistics(response)
        const available = new Set(this.statisticInfo.campaignPerformance.map((campaign) => campaign.name))
        this.selectedCampaigns = this.selectedCampaigns.filter((campaign) => available.has(campaign))
      } catch (error) {
        if (currentRequestId !== this.requestId) return
        console.error(error)
        this.loadError = true
        if (this.$toast) this.$toast.error("Fehler beim Laden der Lead-Statistik")
      } finally {
        if (currentRequestId === this.requestId) this.loading = false
      }
    },
    resetCampaignFilter() {
      this.selectedCampaigns = []
    },
    resetStatistics() {
      this.requestId += 1
      this.loading = false
      this.loadError = false
      this.selectedCampaigns = []
      this.statisticInfo = createEmptyStatistics()
    },
    formatNumber(value) {
      return new Intl.NumberFormat("de-CH").format(Number(value) || 0)
    },
    formatRate(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return "–"
      return `${new Intl.NumberFormat("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(value))} %`
    },
    formatDate(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (match) return `${match[3]}.${match[2]}.${match[1]}`
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "–"
      return new Intl.DateTimeFormat("de-CH").format(date)
    },
    compareNullableRates(valueA, valueB) {
      const a = valueA === null || valueA === undefined ? -1 : Number(valueA)
      const b = valueB === null || valueB === undefined ? -1 : Number(valueB)
      return a - b
    },
    referralBarWidth(value) {
      if (!this.maxReferralLeads) return 0
      return Math.max(5, (value / this.maxReferralLeads) * 100)
    },
    membercardBarWidth(value) {
      if (!this.maxMembercardLeads) return 0
      return Math.max(5, (value / this.maxMembercardLeads) * 100)
    },
    studioInitials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    },
    async downloadStudioConversionExcel() {
      if (this.loading) {
        if (this.$toast) this.$toast.info("Die Statistik wird noch aktualisiert")
        return
      }
      if (this.datePickerRange.length !== 2) {
        if (this.$toast) this.$toast.info("Bitte zuerst einen Zeitraum auswählen")
        return
      }
      try {
        await api.fetchStudioConversionExcel({
          start_date: this.datePickerRange[0],
          end_date: this.datePickerRange[1],
          studios: this.studios.map((studio) => studio._id),
          campaigns: this.selectedCampaigns,
        })
        if (this.$toast) this.$toast.success("Studio Conversion Liste wird heruntergeladen")
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Export der Studio Conversion Liste")
      }
    },
    async downloadSelectedExcel() {
      if (!this.selectedCampaigns.length) {
        if (this.$toast) this.$toast.info("Bitte zuerst mindestens ein Formular auswählen")
        return
      }
      await this.downloadExcel(this.selectedCampaigns, "leadStatistik_formularauswahl.xlsx")
    },
    async downloadExcel(campaigns = [], fileName = "leadStatistik") {
      if (this.loading) {
        if (this.$toast) this.$toast.info("Die Statistik wird noch aktualisiert")
        return
      }
      if (this.datePickerRange.length !== 2) {
        if (this.$toast) this.$toast.info("Bitte zuerst einen Zeitraum auswählen")
        return
      }
      const selectedCampaigns = Array.isArray(campaigns) ? campaigns : []
      try {
        await api.fetchLeadExcel({
          start_date: this.datePickerRange[0],
          end_date: this.datePickerRange[1],
          studios: this.studios.map((studio) => studio._id),
          campaigns: selectedCampaigns,
        }, fileName)
        if (this.$toast) {
          this.$toast.success(selectedCampaigns.length ? "Rohdaten der Formularauswahl werden heruntergeladen" : "Download gestartet")
        }
      } catch (error) {
        if (this.$toast) this.$toast.error("Fehler beim Download")
      }
    },
  },
  watch: {
    datePickerRange: {
      handler() {
        if (this.datePickerRange.length === 2) this.fetchLeadStatistics()
        else this.resetStatistics()
      },
      deep: true,
    },
    studios: {
      handler() {
        if (this.datePickerRange.length === 2) this.fetchLeadStatistics()
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.lead-statistics {
  --surface: #151923;
  --surface-raised: #1a1f2b;
  --surface-soft: #202634;
  --border: rgba(255, 255, 255, 0.075);
  --text: #f4f6fa;
  --text-soft: #a7afbf;
  --text-muted: #737d90;
  --purple: #7c6cff;
  --green: #42d5a0;
  --blue: #42a5f5;
  --orange: #e2a84a;
  position: relative;
  color: var(--text);
}

.dashboard-loader {
  position: sticky;
  top: 0;
  z-index: 30;
  border-radius: 99px;
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 22px;
}

.header-copy {
  min-width: 0;
}

.eyebrow,
.section-kicker,
.panel-kicker {
  color: #9b90ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 0 5px rgba(66, 213, 160, 0.11);
}

.dashboard-header h1 {
  margin: 0;
  color: var(--text);
  font-size: clamp(1.85rem, 3vw, 2.65rem);
  font-weight: 760;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.dashboard-header p {
  max-width: 690px;
  margin: 9px 0 14px;
  color: var(--text-soft);
  font-size: 0.98rem;
}

.context-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.context-pill,
.scope-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 30px;
  padding: 5px 10px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.035);
  color: #c8ced9;
  font-size: 0.77rem;
  font-weight: 600;
}

.context-pill .v-icon {
  color: #8f98aa;
}

.context-pill--active {
  border-color: rgba(124, 108, 255, 0.35);
  background: rgba(124, 108, 255, 0.1);
  color: #d3ceff;
}

.header-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  padding-top: 5px;
}

.dashboard-action.v-btn {
  height: 42px;
  border-color: rgba(255, 255, 255, 0.15);
  border-radius: 11px;
  color: #e5e8ef;
  text-transform: none;
  letter-spacing: 0;
}

.dashboard-action--primary.v-btn {
  background: var(--purple) !important;
  color: white;
  box-shadow: 0 10px 24px rgba(87, 72, 212, 0.24);
}

.action-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 21px;
  height: 21px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(124, 108, 255, 0.22);
  color: #d9d5ff;
  font-size: 0.72rem;
  font-weight: 800;
}

.dashboard-action--primary .action-count {
  background: rgba(255, 255, 255, 0.18);
  color: white;
}

.filter-card,
.export-list {
  border: 1px solid rgba(255, 255, 255, 0.09) !important;
  background: #1b202c !important;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42) !important;
}

.filter-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 12px;
  font-size: 1rem;
}

.filter-title small {
  display: block;
  margin-top: 3px;
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 400;
}

.filter-help {
  margin-top: 12px;
  color: #858fa2;
  font-size: 0.75rem;
  line-height: 1.5;
}

.filter-actions {
  border-top: 1px solid var(--border);
}

.definition-strip,
.state-card {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 17px;
  padding: 13px 15px;
  border: 1px solid rgba(124, 108, 255, 0.2);
  border-radius: 13px;
  background: linear-gradient(90deg, rgba(124, 108, 255, 0.1), rgba(124, 108, 255, 0.025));
}

.definition-icon {
  display: grid;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 10px;
  background: rgba(124, 108, 255, 0.15);
}

.definition-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.definition-copy strong,
.state-card strong {
  color: #e6e3ff;
  font-size: 0.84rem;
  font-weight: 700;
}

.definition-copy span,
.state-card span {
  color: #949db0;
  font-size: 0.76rem;
}

.info-button {
  display: grid;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8f98aa;
  cursor: pointer;
}

.info-button:hover,
.info-button:focus-visible {
  background: rgba(255, 255, 255, 0.06);
  color: white;
  outline: none;
}

::v-deep .info-button .v-icon {
  color: currentColor !important;
}

.state-card--error {
  border-color: rgba(255, 88, 112, 0.25);
  background: rgba(255, 88, 112, 0.08);
}

.state-card--error > div,
.state-card--empty > div {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 13px;
  margin-bottom: 32px;
}

.kpi-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  padding: 17px 18px 16px;
  border: 1px solid var(--border);
  border-radius: 15px;
  background: var(--surface);
}

.kpi-card::before {
  position: absolute;
  top: 0;
  right: 0;
  width: 96px;
  height: 96px;
  border-radius: 0 0 0 100%;
  background: var(--kpi-glow);
  content: "";
  opacity: 0.52;
}

.kpi-card--purple {
  --kpi-accent: #9d91ff;
  --kpi-glow: rgba(124, 108, 255, 0.16);
}
.kpi-card--green {
  --kpi-accent: #56dbae;
  --kpi-glow: rgba(66, 213, 160, 0.14);
}
.kpi-card--blue {
  --kpi-accent: #60b7f8;
  --kpi-glow: rgba(66, 165, 245, 0.14);
}
.kpi-card--orange {
  --kpi-accent: #efbb67;
  --kpi-glow: rgba(226, 168, 74, 0.14);
}

.kpi-topline {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #99a2b3;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}

.kpi-topline .v-icon {
  color: var(--kpi-accent);
}

.kpi-value {
  position: relative;
  z-index: 1;
  margin: 11px 0 5px;
  color: var(--text);
  font-size: clamp(1.7rem, 3vw, 2.25rem);
  font-weight: 760;
  letter-spacing: -0.045em;
  line-height: 1;
}

.kpi-meta {
  position: relative;
  z-index: 1;
  overflow: hidden;
  color: #7f899c;
  font-size: 0.72rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-section {
  margin-top: 40px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
}

.section-heading h2 {
  margin: 5px 0 4px;
  color: var(--text);
  font-size: clamp(1.28rem, 2vw, 1.65rem);
  font-weight: 730;
  letter-spacing: -0.03em;
}

.section-heading p {
  max-width: 760px;
  margin: 0;
  color: var(--text-muted);
  font-size: 0.82rem;
}

.scope-badge {
  flex: 0 0 auto;
  border-color: rgba(124, 108, 255, 0.22);
  background: rgba(124, 108, 255, 0.08);
  color: #b8b0ff;
}

.analytics-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.75fr) minmax(300px, 0.8fr);
  gap: 14px;
}

.panel {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--surface);
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 17px 19px 10px;
}

.panel-heading h3 {
  margin: 3px 0 0;
  color: #ebedf3;
  font-size: 1rem;
  font-weight: 680;
}

.panel-note {
  margin-top: 4px;
  color: #778195;
  font-size: 0.7rem;
  text-align: right;
}

.chart-wrap {
  padding: 0 8px 7px;
}

.chart-wrap--trend {
  padding: 0 12px 5px;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px 19px 18px;
}

.status-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 7px;
  color: #a8b0bf;
  font-size: 0.77rem;
}

.status-row__top > span {
  display: flex;
  align-items: center;
  min-width: 0;
}

.status-row__top i {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  margin-right: 8px;
  border-radius: 50%;
}

.status-row__top strong {
  color: #eef0f5;
  font-size: 0.77rem;
  white-space: nowrap;
}

.status-row__top small {
  margin-left: 4px;
  color: #737d90;
  font-size: 0.65rem;
  font-weight: 500;
}

.status-track,
.ranking-track {
  height: 5px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.055);
}

.status-track span,
.ranking-track span {
  display: block;
  height: 100%;
  min-width: 0;
  border-radius: inherit;
  transition: width 0.35s ease;
}

.panel-footnote {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-top: 1px solid var(--border);
  color: #727c8f;
  font-size: 0.68rem;
}

.panel-footnote .v-icon {
  color: #727c8f;
}

.trend-panel,
.campaign-table-panel {
  margin-top: 14px;
}

::v-deep .mode-toggle.v-btn-toggle {
  padding: 3px;
  border: 1px solid var(--border);
  border-radius: 11px !important;
  background: rgba(255, 255, 255, 0.025) !important;
  box-shadow: none !important;
}

::v-deep .mode-toggle.v-btn-toggle > .v-btn.v-btn {
  min-width: 76px !important;
  border: 0 !important;
  border-radius: 8px !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #8e97a9 !important;
  text-transform: none;
  letter-spacing: 0;
}

::v-deep .mode-toggle.v-btn-toggle > .v-btn.v-btn::before {
  opacity: 0 !important;
}

::v-deep .mode-toggle.v-btn-toggle > .v-btn.v-btn--active {
  background: rgba(124, 108, 255, 0.18) !important;
  color: #d7d3ff !important;
}

.empty-state {
  display: flex;
  min-height: 250px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #6f798c;
  font-size: 0.8rem;
}

.empty-state .v-icon {
  color: #4e5768;
  font-size: 34px;
}

.empty-state--compact {
  min-height: 260px;
}

.dashboard-table {
  background: transparent !important;
}

::v-deep .dashboard-table .v-data-table__wrapper {
  border-top: 1px solid var(--border);
}

::v-deep .dashboard-table table {
  min-width: 780px;
}

::v-deep .dashboard-table thead th {
  height: 45px;
  border-bottom-color: var(--border) !important;
  background: rgba(255, 255, 255, 0.017);
  color: #7f899c !important;
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  white-space: nowrap;
}

::v-deep .dashboard-table tbody td {
  height: 55px;
  border-bottom-color: rgba(255, 255, 255, 0.055) !important;
  color: #b8bfcc;
  font-size: 0.76rem;
}

::v-deep .dashboard-table tbody tr:hover {
  background: rgba(124, 108, 255, 0.045) !important;
}

::v-deep .dashboard-table .v-data-footer {
  border-top-color: var(--border) !important;
  color: #778195;
  font-size: 0.72rem;
}

::v-deep .dashboard-table .v-data-table__wrapper::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

::v-deep .dashboard-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
  border-radius: 99px;
  background: #41495a;
}

::v-deep .dashboard-table .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #171b25;
}

.name-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 210px;
}

.name-cell strong {
  color: #e1e5ec;
  font-weight: 620;
}

.name-cell > div {
  display: flex;
  gap: 4px;
}

.micro-tag {
  display: inline-flex;
  padding: 3px 6px;
  border-radius: 6px;
  font-size: 0.58rem;
  font-weight: 750;
  white-space: nowrap;
}

.micro-tag--referral {
  background: rgba(66, 213, 160, 0.11);
  color: #6be0ba;
}

.micro-tag--membercard {
  background: rgba(124, 108, 255, 0.12);
  color: #aaa0ff;
}

.positive-number {
  color: #5cddb1;
}
.warning-number {
  color: #efbb67;
}
.muted-number {
  color: #5d6677;
}

.rate-pill,
.secondary-rate,
.special-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  padding: 4px 7px;
  border-radius: 7px;
  font-size: 0.7rem;
  font-weight: 750;
}

.rate-pill {
  background: rgba(66, 213, 160, 0.11);
  color: #65dfb8;
}

.secondary-rate {
  color: #b8c0cd;
  background: rgba(255, 255, 255, 0.045);
}

.special-section {
  scroll-margin-top: 16px;
}

.special-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.special-card {
  min-width: 0;
  overflow: hidden;
  padding: 19px;
  border: 1px solid var(--border);
  border-radius: 17px;
  background: var(--surface);
}

.special-card--membercard {
  border-color: rgba(124, 108, 255, 0.19);
  background: radial-gradient(circle at 100% 0, rgba(124, 108, 255, 0.13), transparent 34%), var(--surface);
}

.special-card--referral {
  border-color: rgba(66, 213, 160, 0.16);
  background: radial-gradient(circle at 100% 0, rgba(66, 213, 160, 0.1), transparent 34%), var(--surface);
}

.special-card__header {
  display: flex;
  align-items: center;
  gap: 11px;
}

.special-icon {
  display: grid;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: rgba(124, 108, 255, 0.14);
}

.special-card--membercard .special-icon .v-icon {
  color: #aaa0ff;
}
.special-card--referral .special-icon {
  background: rgba(66, 213, 160, 0.11);
}
.special-card--referral .special-icon .v-icon {
  color: #61dfb7;
}

.special-card__header > div:nth-child(2) {
  min-width: 0;
}

.special-card__header span {
  color: #7e8799;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.special-card__header h3 {
  margin: 1px 0 0;
  color: #eef0f5;
  font-size: 1.05rem;
  font-weight: 700;
}

.special-card__header .special-label {
  margin-left: auto;
  padding: 4px 7px;
  border: 1px solid rgba(124, 108, 255, 0.24);
  border-radius: 7px;
  color: #aaa0ff;
  white-space: nowrap;
}

.special-card--referral .special-label {
  border-color: rgba(66, 213, 160, 0.22);
  color: #61dfb7;
}

.special-intro {
  margin: 14px 0 15px;
  color: #858fa1;
  font-size: 0.77rem;
  line-height: 1.5;
}

.special-kpis span {
  color: #7e8799;
  font-size: 0.65rem;
}

.special-kpis strong {
  margin-top: 3px;
  color: #f1f2f6;
  font-size: 1.35rem;
  line-height: 1.05;
}

.data-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0 16px;
  padding: 10px;
  border-radius: 9px;
  background: rgba(124, 108, 255, 0.07);
  color: #8f97aa;
  font-size: 0.67rem;
  line-height: 1.45;
}

.data-note .v-icon {
  color: #9287f0;
}
.data-note--membercard {
  margin: 16px 0 0;
}
.data-note--referral {
  margin: 16px 0 0;
  background: rgba(66, 213, 160, 0.065);
}
.data-note--referral .v-icon {
  color: #4ed4a6;
}

.mini-list-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #d9dde6;
  font-size: 0.75rem;
  font-weight: 650;
}

.mini-list-heading small {
  color: #646e80;
  font-size: 0.62rem;
  font-weight: 500;
}

.special-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin-bottom: 18px;
}

.special-kpis > div {
  display: flex;
  min-height: 76px;
  justify-content: center;
  flex-direction: column;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.075);
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 225px;
}

.ranking-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 7px;
}

.ranking-row__top strong {
  overflow: hidden;
  color: #ccd2dc;
  font-size: 0.72rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-row__top span {
  color: #737d90;
  font-size: 0.64rem;
  white-space: nowrap;
}

.ranking-row__top > b {
  flex: 0 0 auto;
  color: #aaa0ff;
  font-size: 0.68rem;
  font-weight: 700;
}

.ranking-row__details {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 12px;
  margin: -2px 0 7px;
  color: #737d90;
  font-size: 0.62rem;
}

.ranking-row__top b {
  color: #62dcb5;
}
.ranking-track span {
  background: linear-gradient(90deg, #2ea77e, #52dab0);
}
.ranking-list--membercard .ranking-row__top > b {
  color: #aaa0ff;
}
.ranking-list--membercard .ranking-track span {
  background: linear-gradient(90deg, #7465eb, #a79dff);
}

.special-empty {
  display: grid;
  min-height: 110px;
  place-items: center;
  color: #636d80;
  font-size: 0.72rem;
  text-align: center;
}

.show-more.v-btn {
  margin: 8px 0 0 -8px;
  text-transform: none;
  letter-spacing: 0;
}

.section-heading--controls {
  align-items: flex-end;
}

.table-controls {
  display: grid;
  width: min(100%, 470px);
  grid-template-columns: minmax(180px, 1fr) minmax(170px, 0.8fr);
  gap: 9px;
}

::v-deep .table-controls .v-input,
::v-deep .filter-card .v-input {
  color: white;
}

::v-deep .table-controls .v-input input,
::v-deep .table-controls .v-select__selection {
  color: #d6dbe5 !important;
  caret-color: #9b90ff !important;
}

::v-deep .table-controls .v-icon {
  color: #818b9e !important;
}

::v-deep .table-controls .v-label,
::v-deep .filter-card .v-label {
  color: #8790a2;
  font-size: 0.8rem;
}

::v-deep .table-controls .v-input__slot,
::v-deep .filter-card .v-input__slot {
  border-radius: 10px !important;
}

::v-deep .table-controls .v-input__slot {
  background: rgba(17, 21, 29, 0.48) !important;
}

::v-deep .table-controls .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.13) !important;
}

::v-deep .table-controls .v-input--is-focused fieldset {
  border-color: #8276e5 !important;
}

::v-deep .table-controls .v-input--is-focused .v-label,
::v-deep .table-controls .v-input--is-focused .v-icon {
  color: #aaa0ff !important;
}

.table-scope-note {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 17px;
  color: #7c8698;
  font-size: 0.69rem;
}

.table-scope-note .v-icon {
  color: #8276e5;
}

.studio-name {
  display: flex;
  min-width: 170px;
  align-items: center;
  gap: 10px;
}

.studio-name > span {
  display: grid;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  place-items: center;
  border-radius: 9px;
  background: rgba(124, 108, 255, 0.12);
  color: #aaa1fa;
  font-size: 0.63rem;
  font-weight: 800;
}

.studio-name strong {
  overflow: hidden;
  color: #e0e4ec;
  font-weight: 620;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.special-count--membercard {
  min-width: 38px;
  background: rgba(124, 108, 255, 0.09);
  color: #a99fff;
}
.special-count--referral {
  min-width: 38px;
  background: rgba(66, 213, 160, 0.08);
  color: #5ddbb2;
}

.studio-mobile-grid {
  display: none;
}

.dashboard-footnote {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 18px 0 4px;
  padding: 0 4px;
  color: #677184;
  font-size: 0.68rem;
  line-height: 1.5;
}

.dashboard-footnote .v-icon {
  color: #677184;
}

@media (max-width: 1180px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .analytics-grid {
    grid-template-columns: minmax(0, 1.45fr) minmax(285px, 0.8fr);
  }
  .special-grid {
    grid-template-columns: 1fr;
  }
  .special-card {
    padding: 20px;
  }
}

@media (max-width: 860px) {
  .dashboard-header {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
  .header-actions {
    padding-top: 0;
  }
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  .section-heading--controls {
    align-items: stretch;
    flex-direction: column;
  }
  .table-controls {
    width: 100%;
  }
  .status-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .empty-state--compact {
    min-height: 160px;
  }
}

@media (max-width: 720px) {
  .dashboard-header p {
    font-size: 0.88rem;
  }
  .definition-strip {
    align-items: flex-start;
  }
  .definition-icon {
    width: 32px;
    height: 32px;
  }
  .definition-copy strong {
    font-size: 0.77rem;
  }
  .definition-copy span {
    margin-top: 2px;
    font-size: 0.7rem;
  }
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
  .panel-heading--responsive {
    align-items: stretch;
    flex-direction: column;
  }
  .mode-toggle {
    align-self: flex-start;
  }
  .special-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .studio-table--desktop {
    display: none;
  }
  .studio-mobile-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 0 12px 12px;
  }
  .studio-mobile-card {
    min-width: 0;
    padding: 14px;
    border: 1px solid var(--border);
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.02);
  }
  .studio-mobile-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }
  .studio-mobile-card .studio-name {
    min-width: 0;
  }
  .mobile-conversion {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-end;
    flex-direction: column;
  }
  .mobile-conversion small {
    color: #697386;
    font-size: 0.58rem;
    text-transform: uppercase;
  }
  .mobile-conversion strong {
    color: #5ddbb2;
    font-size: 0.95rem;
  }
  .studio-mobile-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 13px 0;
  }
  .studio-mobile-stats div {
    display: flex;
    flex-direction: column;
  }
  .studio-mobile-stats span {
    color: #697386;
    font-size: 0.59rem;
  }
  .studio-mobile-stats strong {
    margin-top: 2px;
    color: #d7dbe4;
    font-size: 0.82rem;
  }
  .studio-mobile-specials {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    color: #808a9d;
    font-size: 0.62rem;
  }
  .studio-mobile-specials span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .studio-mobile-specials i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  .dot-membercard {
    background: #8879ff;
  }
  .dot-referral {
    background: #42d5a0;
  }
}

@media (max-width: 520px) {
  .header-actions {
    width: 100%;
  }
  .dashboard-action.v-btn {
    flex: 1;
  }
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
  }
  .kpi-card {
    padding: 14px 13px;
  }
  .kpi-topline {
    align-items: flex-start;
    font-size: 0.62rem;
    line-height: 1.25;
  }
  .kpi-topline .v-icon {
    font-size: 19px !important;
  }
  .kpi-value {
    font-size: 1.55rem;
  }
  .kpi-meta {
    font-size: 0.62rem;
    white-space: normal;
  }
  .dashboard-section {
    margin-top: 33px;
  }
  .panel-heading {
    padding: 15px 14px 9px;
  }
  .panel-note {
    display: none;
  }
  .status-list {
    grid-template-columns: 1fr;
    padding: 14px;
  }
  .special-card {
    padding: 15px;
  }
  .special-card__header {
    align-items: flex-start;
  }
  .special-card__header .special-label {
    font-size: 0.57rem;
  }
  .special-icon {
    width: 38px;
    height: 38px;
  }
  .special-kpis strong {
    font-size: 1.13rem;
  }
  .table-controls {
    grid-template-columns: 1fr;
  }
  .studio-mobile-grid {
    grid-template-columns: 1fr;
  }
  .table-scope-note {
    align-items: flex-start;
  }
}

@media (max-width: 370px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  .action-label {
    display: none;
  }
  .dashboard-action .v-icon {
    margin-right: 0 !important;
  }
}
</style>
